/*
  <casalta-search> — the header search panel.

  Own implementation, not Dawn's predictive-search.js. That file is welded to
  Dawn's markup and component-predictive-search.css, both of which this theme
  has already emptied. This is smaller and we own the markup.

  Two sources, because no single one covers both:

    Products  Shopify's /search/suggest.json. Always current, and it does
              real matching — "glasses" finds "Sunglasses", which a substring
              filter would not.

    Projects  A JSON list rendered into the page by Liquid. Shopify does not
              index metaobjects in search AT ALL — not predictively, not on
              the results page — so this is the only way to make interiors
              findable. Fine at this scale: a handful of entries, filtered in
              the browser, no round trip.

  Lives in its own asset rather than the header's {% javascript %} block so it
  is loaded by a <script> tag we control. Section JS only runs for sections
  rendered on the page, and this needs to work everywhere the header does.
*/

class CasaltaSearch extends HTMLElement {
  connectedCallback() {
    /* More than one trigger: the header link on desktop, and the Search item
       in the mobile nav panel, which is inside this element too. querySelector
       bound only the first, so the phone menu's Search navigated away instead
       of opening the panel. lastTrigger is whichever one was used, so Escape
       returns focus to the control the visitor actually touched. */
    this.triggers = Array.from(this.querySelectorAll('[data-search-trigger]'));
    this.trigger = this.triggers[0];
    this.lastTrigger = this.trigger;
    this.panel = this.querySelector('[data-search-panel]');
    this.input = this.querySelector('[data-search-input]');
    this.results = this.querySelector('[data-search-results]');
    this.closeBtn = this.querySelector('[data-search-close]');
    this.more = this.querySelector('[data-search-more]');

    if (!this.trigger || !this.panel || !this.input || !this.results) return;

    this.projects = [];
    const data = this.querySelector('[data-search-projects]');
    if (data) {
      try {
        this.projects = JSON.parse(data.textContent) || [];
      } catch (e) {
        this.projects = [];
      }
    }

    this.limit = parseInt(this.dataset.limit, 10) || 5;
    this.objectsLabel = this.dataset.objectsLabel || 'Objects';
    this.interiorsLabel = this.dataset.interiorsLabel || 'Interiors';
    this.searchUrl = this.dataset.searchUrl || '/search';
    this.open = false;
    this.controller = null;
    this.debounce = null;

    this.triggers.forEach((trigger) => {
      trigger.addEventListener('click', (e) => {
        e.preventDefault();
        this.lastTrigger = trigger;
        this.toggle();
      });
    });

    if (this.closeBtn) {
      this.closeBtn.addEventListener('click', () => this.close());
    }

    this.input.addEventListener('input', () => this.onInput());

    // Escape closes from anywhere inside the panel, including the field
    this.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        this.close();
        this.lastTrigger.focus();
      }
    });

    // A click outside the header closes it
    this.onDocClick = (e) => {
      if (this.open && !this.contains(e.target)) this.close();
    };
    document.addEventListener('click', this.onDocClick);
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.onDocClick);
  }

  toggle() {
    if (this.open) {
      this.close();
      this.lastTrigger.focus();
    } else {
      this.openPanel();
    }
  }

  /* aria-expanded is set on EVERY trigger, not just the one clicked: both
     point at the same panel, so a stale "false" on the other one would
     misreport the state to a screen reader. */
  setExpanded(value) {
    this.triggers.forEach((trigger) => trigger.setAttribute('aria-expanded', value));
  }

  openPanel() {
    this.open = true;
    this.panel.hidden = false;
    this.setExpanded('true');
    this.input.focus();
  }

  close() {
    this.open = false;
    this.panel.hidden = true;
    this.setExpanded('false');
  }

  onInput() {
    const q = this.input.value.trim();

    clearTimeout(this.debounce);

    if (q.length < 2) {
      this.render([], [], '');
      return;
    }

    // Projects filter instantly — the data is already here
    const projects = this.matchProjects(q);
    this.render([], projects, q);

    this.debounce = setTimeout(() => this.fetchProducts(q, projects), 200);
  }

  matchProjects(q) {
    const needle = q.toLowerCase();
    return this.projects
      .filter((p) =>
        [p.title, p.location, p.year, p.category]
          .filter(Boolean)
          .some((v) => String(v).toLowerCase().includes(needle))
      )
      .slice(0, this.limit);
  }

  fetchProducts(q, projects) {
    if (this.controller) this.controller.abort();
    this.controller = new AbortController();

    // Same trailing wildcard the forms add. The predictive API does NOT
    // reliably prefix-match on its own: "flat" finds Sheffield Flatware but
    // "shef" finds nothing, and "eil" misses Eileen Cup. Whatever the rule
    // is, it is not "matches the start of a word" — so ask for the prefix
    // explicitly rather than hoping.
    const url =
      '/search/suggest.json?q=' +
      encodeURIComponent(casaltaAddWildcard(q)) +
      // Ask for the most Shopify will give (10) and filter below: it now
      // returns loosely RELATED products too ("cup" brought back two vases
      // that mention no cup anywhere), so some of what comes back is dropped.
      '&resources[type]=product&resources[limit]=10';

    fetch(url, { signal: this.controller.signal })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        const products = (
          (data && data.resources && data.resources.results && data.resources.results.products) || []
        )
          .filter((p) => casaltaProductMatches(p, q))
          .slice(0, this.limit);
        this.render(products, projects, q);
      })
      .catch(() => {
        /* Aborted or offline — leave the project results standing */
      });
  }

  render(products, projects, q) {
    if (!q) {
      this.results.innerHTML = '';
      if (this.more) this.more.hidden = true;
      return;
    }

    let html = '';

    if (products.length) {
      html += '<p class="casalta-search__group type-label">' + this.escapeHtml(this.objectsLabel) + '</p>';
      html += '<ul class="casalta-search__hits" role="list">';
      products.forEach((p) => {
        const img = p.image
          ? '<img src="' + this.escapeAttr(p.image) + '" alt="" loading="lazy" width="60" height="75">'
          : '';
        html +=
          '<li><a class="casalta-search__hit" href="' +
          this.escapeAttr(p.url) +
          '"><span class="casalta-search__thumb">' +
          img +
          '</span><span class="casalta-search__hit-text"><span class="casalta-search__hit-title">' +
          this.escapeHtml(p.title) +
          '</span><span class="casalta-search__hit-meta">' +
          this.escapeHtml(this.money(p.price)) +
          '</span></span></a></li>';
      });
      html += '</ul>';
    }

    if (projects.length) {
      html += '<p class="casalta-search__group type-label">' + this.escapeHtml(this.interiorsLabel) + '</p>';
      html += '<ul class="casalta-search__hits" role="list">';
      projects.forEach((p) => {
        const img = p.image
          ? '<img src="' + this.escapeAttr(p.image) + '" alt="" loading="lazy" width="60" height="75">'
          : '';
        // Year only — location largely repeated the title. It is still
        // matched on in matchProjects(), just not printed.
        const meta = p.year || '';
        html +=
          '<li><a class="casalta-search__hit" href="' +
          this.escapeAttr(p.url) +
          '"><span class="casalta-search__thumb">' +
          img +
          '</span><span class="casalta-search__hit-text"><span class="casalta-search__hit-title">' +
          this.escapeHtml(p.title) +
          '</span><span class="casalta-search__hit-meta">' +
          this.escapeHtml(meta) +
          '</span></span></a></li>';
      });
      html += '</ul>';
    }

    if (!products.length && !projects.length) {
      html = '<p class="casalta-search__none type-label">No matches</p>';
    }

    this.results.innerHTML = html;

    if (this.more) {
      this.more.hidden = false;
      // Same wildcard the forms add, so "View all results" finds what the
      // panel just showed rather than fewer things.
      this.more.href = this.searchUrl + '?q=' + encodeURIComponent(casaltaAddWildcard(q));
    }
  }

  money(value) {
    if (value === undefined || value === null || value === '') return '';
    // suggest.json returns a decimal string like "85.00"
    const n = Number(value);
    if (Number.isNaN(n)) return String(value);
    return '$' + (n % 1 === 0 ? n.toFixed(0) : n.toFixed(2));
  }

  escapeHtml(s) {
    return String(s === undefined || s === null ? '' : s).replace(/[&<>"']/g, (c) => ({
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#39;',
    })[c]);
  }

  escapeAttr(s) {
    return this.escapeHtml(s);
  }
}

if (!customElements.get('casalta-search')) {
  customElements.define('casalta-search', CasaltaSearch);
}

/*
  Prefix matching on the results page.

  Shopify's full-text search matches WHOLE WORDS — "coaster" finds the
  coasters, "coa" finds nothing — while the predictive API used by the panel
  prefix-matches on its own. Left alone, the dropdown and the page it links to
  disagree about what matches.

  A trailing * on the last word turns on prefix matching. Only the last word,
  because that is the one still being typed. The section strips the * back out
  before displaying the term.

  With JS off this never runs and search falls back to whole-word matching —
  stricter, but correct. Nothing breaks.
*/
/*
  STRICT MATCHING. Shopify's search now returns related products as well as
  matching ones — "cup" found Eileen Cups and two vases that mention no cup
  at all. A product is kept only when EVERY word typed is the start of a word
  in its title, description, type or tags. "cup" keeps the cups, "lacquer"
  still finds the lacquer pieces by their descriptions, "shef" still finds
  Sheffield. The vendor is left out on purpose: every product is "Casalta",
  so "ca" would match the whole catalogue. The results page applies the same
  rule in Liquid (casalta-search.liquid) — keep the two in step.
*/
function casaltaWords(text) {
  return String(text || '')
    .toLowerCase()
    .replace(/<[^>]*>/g, ' ')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .split(/[^a-z0-9]+/)
    .filter(Boolean);
}

function casaltaProductMatches(p, q) {
  const terms = casaltaWords(q);
  if (!terms.length) return true;
  const words = casaltaWords([p.title, p.body, p.type, (p.tags || []).join(' ')].join(' '));
  return terms.every((t) => words.some((w) => w.startsWith(t)));
}

/*
  SWITCHED OFF 20 Oct 2026. Shopify's search now prefix-matches on its own —
  "eil", "shef", "cass", "bev" all find their products on both endpoints —
  and a trailing * now makes it return EVERY product ("eileen" 2 results,
  "eileen*" 19), so search stopped narrowing. Measured on the live store.
  The function stays, returning the query untouched (any * the visitor typed
  is removed too), so the panel, "View all results" and the forms need no
  other change. Re-test both endpoints before ever turning it back on.
*/
function casaltaAddWildcard(value) {
  return String(value || '').replace(/\*/g, '').trim();
}

document.addEventListener('submit', (e) => {
  const form = e.target.closest && e.target.closest('form[data-search-wildcard]');
  if (!form) return;
  const field = form.querySelector('input[name="q"]');
  if (field) field.value = casaltaAddWildcard(field.value);
});
